import { GeneralService, ApiKeyOrGetter } from "./GeneralService";
import { VastAdapter, VastInstance } from "./VastInfraService";

export type { VastAdapter, VastInstance };

export interface StateHistoryEntry {
  id: number;
  infra_id: number;
  old_status: string | null;
  new_status: string;
  changed_at: string;
  changed_by: string | null;
  meta: Record<string, unknown> | null;
}

export interface ConnectionInfo {
  ssh_host?: string | null;
  ssh_port?: number | null;
  ssh_command?: string | null;
  public_ip?: string | null;
  jupyter_token?: string | null;
  vllm_url?: string | null;
  ports?: Record<string, string>;
}

export interface InfraInstanceDetail extends VastInstance {
  hf_model_id?: string | null;
  vllm_config?: Record<string, unknown>;
  state_history?: StateHistoryEntry[];
  total_running_seconds?: number;
  total_running_hours?: number;
  estimated_cost_usd?: number;
  connection_info?: ConnectionInfo | null;
  uptime_segments?: Array<{
    state: string;
    start: string;
    end: string;
    seconds: number;
    open?: boolean;
  }>;
}

export interface CreateInfraPayload {
  provider: string;
  gpu_profile: string;
  gpu_count: number;
  hf_model_id: string;
  base_model?: string;
  display_name?: string;
  template_hash_id?: string;
  vllm_config?: Record<string, unknown>;
  max_adapter_count?: number;
  disk_gb?: number;
  company_id?: number;
}

export interface VastTemplate {
  name: string;
  hash_id: string;
}

export interface InstancesStats {
  running: number;
  provisioning: number;
  total_adapters: number;
}

export interface PaginatedInstances {
  items: VastInstance[];
  total: number;
  offset: number;
  limit: number;
  stats: InstancesStats;
}

export interface ModelRequirements {
  hf_model_id: string;
  parameters: number;
  parameters_human: string;
  dtype: string;
  weight_size_gb: number;
  estimated_vram_bf16_gb: number;
  estimated_vram_fp8_gb: number;
  recommendations: Array<{
    gpu_profile: string;
    gpu_count: number;
    total_vram_gb: number;
    headroom_gb: number;
    available: boolean;
    min_dph: number | null;
  }>;
  gpu_availability: Record<string, Record<number, { available: boolean; min_dph: number | null }>>;
}

export interface VllmConfigSuggestion {
  max_model_len: number;
  max_num_seqs: number;
  max_num_batched_tokens: number;
  kv_cache_dtype: string;
  load_format: string;
  tokenizer_mode: string;
  config_format: string;
  gdn_prefill_backend: string;
  enable_auto_tool_choice: boolean;
  tool_call_parser: string;
}

export class AdminInfraService {
  public static suggestVllmConfig(
    hfModelId: string,
    optimizeFor: "throughput" | "latency",
    estimatedInputTokens: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/admin/infra/suggest-vllm-config?hf_model_id=${encodeURIComponent(hfModelId)}&optimize_for=${optimizeFor}&estimated_input_tokens=${estimatedInputTokens}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getModelRequirements(
    hfModelId: string,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/admin/infra/model-requirements?hf_model_id=${encodeURIComponent(hfModelId)}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getTemplates(
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      "/admin/infra/templates",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getInstances(
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      "/admin/infra/instances",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getInstancesPaginated(
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/admin/infra/instances?offset=${offset}&limit=${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getInstance(
    infraId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/admin/infra/instances/${infraId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createInstance(
    payload: CreateInfraPayload,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/admin/infra/instances",
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static updateVllmConfig(
    infraId: number,
    vllmConfig: Record<string, unknown>,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/admin/infra/instances/${infraId}/vllm-config`,
      JSON.stringify({ vllm_config: vllmConfig }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static startInstance(
    infraId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/admin/infra/instances/${infraId}/start`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static stopInstance(
    infraId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/admin/infra/instances/${infraId}/stop`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static destroyInstance(
    infraId: number,
    force: boolean,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/admin/infra/instances/${infraId}/destroy`,
      JSON.stringify({ force }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getLogs(
    infraId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/admin/infra/instances/${infraId}/logs`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static removeAdapter(
    infraId: number,
    modelId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/admin/infra/instances/${infraId}/adapters/${modelId}`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}
